import * as angular from 'angular';
import * as _ from 'lodash';
import './login.scss';

export class LoginController {
    public anchor: string;
    public message: string;

    public username: string;
    public password: string;
    public rememberMe: boolean;
    public hasError: boolean;
    public isBusy: boolean;
    public redirectUrl: string;
    public widgetId: any;
    public captchaToken: any;

    constructor(
        private readonly $q: ng.IQService,
        private readonly $log: ng.ILogService,
        private readonly $window: ng.IWindowService,
        private readonly authService: any,
        private readonly $state: ng.ui.IStateService,
        private readonly $scope: ng.IScope,
        private readonly $timeout: ng.ITimeoutService,
        private readonly dataContext: any,
        private readonly $cookies: any,
        private readonly constants: any
    ) {
        'ngInject';

        this.username = null;
        this.password = null;
        this.rememberMe = false;
        this.hasError = false;
        this.redirectUrl = null;
        this.isBusy = false;
        this.captchaToken = null;
    }

    public $onInit() {
        this.loadTurnstileScript().then(() => {
            this.$timeout(() => this.renderTurnstile());
        });
    }

    private loadTurnstileScript(): ng.IPromise<void> {
        var deferred = this.$q.defer<void>();

        if ((window as any).turnstile) {
            deferred.resolve();
            return deferred.promise;
        }

        var script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        script.async = true;
        script.defer = true;
        script.onload = () => deferred.resolve();
        script.onerror = () => deferred.reject();
        document.head.appendChild(script);

        return deferred.promise;
    }

    private renderTurnstile() {
        var container = document.getElementById("loginTurnstile");
        if (!container) return;

        if (this.widgetId != null && (window as any).turnstile) {
            (window as any).turnstile.remove(this.widgetId);
        }

        this.captchaToken = null;

        this.widgetId = (window as any).turnstile.render(container, {
            sitekey: this.constants.turnstileSiteKey,
            size: 'compact',
            appearance: 'always',
            callback: (token: string) => {
                this.captchaToken = token;
                this.$scope.$apply();
            },
            'expired-callback': () => {
                this.captchaToken = null;
                this.$scope.$apply();
            },
            'error-callback': () => {
                this.captchaToken = null;
                this.$scope.$apply();
            }
        });
    }

    public formFieldsPopulated() {
        return (!_.isNil(this.username) && !_.isNil(this.password));
    }

    public $onDestroy() {
        if (this.widgetId != null && (window as any).turnstile) {
            (window as any).turnstile.remove(this.widgetId);
            this.widgetId = null;
        }
    }

    public submit() {
        if (!this.captchaToken) {
            this.showError('Verification is currently in progress. Please wait a moment and try again.');
            return;
        }

        this.isBusy = true;
        this.showError(null);
        const urlParams = new URLSearchParams(window.location.search);
        this.redirectUrl = urlParams.get('redirectTo');

        this.dataContext.user.login(this.username, this.password, this.captchaToken, this.rememberMe, this.redirectUrl)
            .then((loginResult: any) => {

                const today = new Date();
                const expiresValue = new Date(today);
                expiresValue.setMinutes(today.getMinutes() + this.constants.cache.expiry);
                this.$cookies.put(
                    'IsFPCustomer',
                    true,
                    {
                        expires: expiresValue,
                        domain: 'frontpointsecurity.com'
                    });

                if (!_.isEmpty(loginResult)) {
                    this.authService.loginConfirmed();
                    
                    if (this.redirectUrl && this.redirectUrl.indexOf('supportkbredirect') != -1) {
                        var baseUrl = 'https://help.frontpointsecurity.com';
                        const tokenCookie = this.$cookies.get('GlobalFPToken');

                        const target = urlParams.get('target');
                        if (target) {
                            baseUrl = target;
                        }

                        this.$window.open(`${baseUrl}?${tokenCookie ? `t=${tokenCookie}&` : ''}`, '_self');
                    }
                    else {
                        this.$window.open(loginResult, '_self');
                    }
                }
            },
               (error: any) => {
                    // Reset turnstile to get a fresh token for retry
                    this.captchaToken = null;
                    if (this.widgetId != null && (window as any).turnstile) {
                        (window as any).turnstile.reset(this.widgetId);
                    }
                    this.$log.error(error);
                    let errorMessage = error;
                    if (!_.isNil(error) && !_.isNil(error.data)) {
                        if (!_.isEmpty(error.data.RedirectUrl)) {
                            this.$window.location.href = decodeURIComponent(error.data.RedirectUrl).replace('#', '');
                            errorMessage = null;
                        } else if (!_.isEmpty(error.data.Reason)) {
                            errorMessage = error.data.Reason;
                        } else if (!_.isEmpty(error.data.Message)) {
                            errorMessage = error.data.Message;
                        } else {
                            errorMessage = error.data;
                        }
                    }

                    if (!_.isNil(errorMessage) &&
                        errorMessage.toString().toLowerCase() === 'password change required') {
                        return this.$state.go('login.changePassword', {
                            message: 'Password change required'
                        });
                    } else {
                        this.showError(errorMessage);
                        return this.$q.reject(error);
                    }
                })
            .finally(() => {
                this.isBusy = false;
            });
    }

    public showError(message: string) {
        this.hasError = !_.isEmpty(message);
        this.message = message;
    }
}

export const loginComponent = {
    template: require('./login.html'),
    bindings: {
        message: '<'
    },
    controller: LoginController
};
